import { usePmfSurveySteps } from '@griply/hooks';
import { isPmfSurveyEligible } from '@griply/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useRecordPmfActivity } from 'shared/hooks/use-record-pmf-activity';
import { useSubmitPmfSurvey } from 'shared/hooks/use-submit-pmf-survey';
import { useUser } from 'shared/hooks/use-user';

import { useRequestPushNotifications } from '../request-push-notifications';
import { PmfSurveyMessage } from './pmf-survey-message';

export type PmfSurveyGateProps = {
  children: React.ReactNode;
};

export const PmfSurveyGate: React.FC<PmfSurveyGateProps> = ({ children }) => {
  const user = useUser();
  const { isMessageOpen } = useRequestPushNotifications();
  const { recordSessionActivity } = useRecordPmfActivity();
  const { answerDisappointment, answerText, dismiss } = useSubmitPmfSurvey();

  const [isOpen, setIsOpen] = useState(false);
  const shownThisSession = useRef(false);

  const close = () => setIsOpen(false);

  const {
    step,
    disappointment,
    showThankYou,
    selectOption,
    submitText,
    reset,
  } = usePmfSurveySteps({
    answerDisappointment,
    answerText,
    onFinished: close,
  });

  const handleClose = () => {
    if (!disappointment) {
      dismiss();
    }
    close();
  };

  useEffect(() => {
    recordSessionActivity();
  }, [recordSessionActivity]);

  useEffect(() => {
    if (!user || isOpen || shownThisSession.current || isMessageOpen) {
      return;
    }
    if (
      !isPmfSurveyEligible({
        pmfSurvey: user.pmfSurvey,
        usageStatistics: user.usageStatistics,
        now: new Date(),
      })
    ) {
      return;
    }
    shownThisSession.current = true;
    reset();
    setIsOpen(true);
  }, [user, isOpen, isMessageOpen, reset]);

  return (
    <>
      {children}
      {isOpen && !isMessageOpen && (
        <PmfSurveyMessage
          step={step}
          showThankYou={showThankYou}
          onSelectOption={selectOption}
          onSubmitText={submitText}
          onClose={handleClose}
        />
      )}
    </>
  );
};
